<template>
  <div>
    <div class="main-content">
      <div class="card">
        <div class="card-header border-bottom">
          <h4 class="card-title mb-0">{{ role.id ? "Editar Papel" : "Novo Papel" }}</h4>
        </div>
        <div class="card-body pt-2">
          <b-alert :show="!editable" variant="warning" class="p-1">
            Este é um papel universal: somente a IT4D pode alterá-lo. As habilidades abaixo são só para consulta.
          </b-alert>

          <ValidationObserver ref="formObserver" v-slot="{ validate }">
            <b-form @submit.prevent="handleSubmit(validate)">
              <div class="form-row">
                <ValidationProvider name="Nome" rules="required" v-slot="{ errors }" class="form-group col-md-8">
                  <label for="roleName"><span class="required">*</span> Nome:</label>
                  <b-form-input
                    id="roleName"
                    v-model="role.name"
                    :disabled="!editable"
                    :state="errors.length ? false : null"
                  />
                  <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
                </ValidationProvider>

                <div class="form-group col-md-4">
                  <label for="roleStatus">{{ $t("Status") }}:</label>
                  <b-form-select id="roleStatus" v-model="role.status" :disabled="!editable || role.slug === 'master'">
                    <option :value="1">{{ $t("Active") }}</option>
                    <option :value="0">{{ $t("Inactive") }}</option>
                  </b-form-select>
                </div>

                <div class="form-group col-md-12">
                  <label for="roleDescription">Descrição:</label>
                  <b-form-textarea
                    id="roleDescription"
                    v-model="role.description"
                    rows="3"
                    :disabled="!editable"
                  />
                </div>

                <div class="form-group col-md-12" v-if="isSuperAdmin">
                  <b-form-checkbox v-model="role.universal" :disabled="!editable || role.is_system">
                    Papel universal
                    <span class="text-muted">— disponível para todas as empresas</span>
                  </b-form-checkbox>
                  <small v-if="!role.universal" class="text-muted d-block mt-25">
                    Desmarcado, o papel pertence só à empresa ativa ({{ $store.state.user.company_name }}).
                  </small>
                  <b-form-checkbox
                    v-if="role.universal"
                    v-model="role.is_super_admin"
                    class="mt-1"
                    :disabled="!editable || role.is_system"
                  >
                    Super Admin
                    <span class="text-muted">— libera todas as habilidades, inclusive as criadas futuramente</span>
                  </b-form-checkbox>
                </div>
              </div>

              <span class="required_fields">
                <span class="required">*</span>
                <strong>{{ $t("Required Fields") }}</strong>
              </span>
              <div class="block text-right">
                <b-button variant="outline-secondary" class="btn btn-back" @click="$router.push('/roles')">
                  <i class="fa fa-arrow-left"></i> {{ $t("To Go Back") }}
                </b-button>
                <b-button v-if="editable" type="submit" variant="primary" class="ml-1">
                  {{ $t("Save") }}
                  <i class="fa fa-save"></i>
                </b-button>
              </div>
            </b-form>
          </ValidationObserver>
        </div>
      </div>

      <div class="card">
        <div class="card-header border-bottom">
          <h4 class="card-title mb-0">Habilidades</h4>
          <small class="text-muted">{{ role.abilities.length }} de {{ totalAbilities }} selecionadas</small>
        </div>
        <div class="card-body pt-2">
          <b-alert :show="role.is_super_admin" variant="info" class="p-1">
            Super Admin tem todas as habilidades automaticamente.
          </b-alert>

          <div class="row">
            <div
              v-for="group in groups"
              :key="group.key"
              class="col-xl-3 col-lg-4 col-md-6 mb-2"
            >
              <div class="ability-group h-100">
                <div class="ability-group-header">
                  <span class="ability-group-title">
                    <feather-icon :icon="group.icon || 'CircleIcon'" class="mr-50" />
                    {{ group.name }}
                  </span>
                  <span class="ability-group-tools">
                    <span class="text-muted mr-50">{{ countSelected(group) }}/{{ group.items.length }}</span>
                    <a
                      v-if="editable && !role.is_super_admin"
                      href="#"
                      @click.prevent="toggleGroup(group)"
                    >{{ isGroupFull(group) ? "Desmarcar todas" : "Marcar todas" }}</a>
                  </span>
                </div>

                <div
                  v-for="item in group.items"
                  :key="item.slug"
                  class="ability-item"
                  :class="{ 'ability-item-locked': isLocked(item.slug) }"
                  v-b-tooltip.hover.top="isLocked(item.slug) && editable ? 'Você não possui esta habilidade, então não pode concedê-la.' : ''"
                >
                  <b-form-checkbox
                    :checked="isChecked(item.slug)"
                    :disabled="!editable || role.is_super_admin || isLocked(item.slug)"
                    @change="(checked) => toggleAbility(item.slug, checked)"
                  >
                    {{ item.name }}
                    <b-badge v-if="isRestriction(item.slug)" variant="light-warning" class="ml-25">restrição</b-badge>
                  </b-form-checkbox>
                  <span class="ability-slug">{{ item.slug }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import {
  BAlert, BBadge, BButton, BForm, BFormCheckbox, BFormInput, BFormInvalidFeedback, BFormSelect, BFormTextarea, VBTooltip,
} from "bootstrap-vue";
import axios from "axios";

extend("required", required);

export default {
  data() {
    return {
      role: {
        id: null,
        name: "",
        description: "",
        status: 1,
        universal: false,
        is_super_admin: false,
        is_system: false,
        slug: null,
        abilities: [],
        editable: true,
      },
      originalAbilities: [],
      groups: [],
      restricoes: [],
    };
  },
  computed: {
    isSuperAdmin() {
      return this.$store.state.user.is_super_admin;
    },
    editable() {
      return this.role.editable;
    },
    totalAbilities() {
      return this.groups.reduce((total, group) => total + group.items.length, 0);
    },
  },
  methods: {
    async handleSubmit(validate) {
      const isValid = await validate();
      if (isValid) {
        this.save();
      }
    },
    save() {
      let api = this.$store.state.api + "roles";
      const payload = {
        name: this.role.name,
        description: this.role.description,
        status: this.role.status,
        universal: this.role.universal,
        is_super_admin: this.role.universal && this.role.is_super_admin,
        abilities: this.role.is_super_admin ? [] : this.role.abilities,
      };

      if (this.role.id) {
        payload._method = "PUT";
        api += "/" + this.role.id;
      }

      axios
        .post(api, payload)
        .then(() => {
          this.$message(this.$t("Success"), this.$t("Information saved successfully"), "success");
          this.$store.commit("profileUpdate");
          this.$router.push("/roles");
        })
        .catch((error) => {
          this.$error(error);
        });
    },
    getCatalog() {
      return axios.get(this.$store.state.api + "roles/abilities").then((response) => {
        this.restricoes = response.data.restricoes || [];
        this.groups = response.data.groups.map((group) => ({
          key: group.key,
          name: group.name,
          icon: group.icon,
          items: Object.keys(group.abilities).map((slug) => ({ slug, name: group.abilities[slug] })),
        }));
      });
    },
    getRole(id) {
      axios
        .get(this.$store.state.api + "roles/" + id)
        .then((response) => {
          const role = response.data.data[0];
          this.role = { ...this.role, ...role, status: Number(role.status) };
          this.originalAbilities = [...role.abilities];
        })
        .catch((error) => {
          this.$error(error);
        });
    },
    isChecked(slug) {
      return this.role.is_super_admin || this.role.abilities.includes(slug);
    },
    isRestriction(slug) {
      return this.restricoes.includes(slug);
    },
    // Fora a IT4D, só se concede o que se tem; o que já estava no papel pode ficar.
    isLocked(slug) {
      if (this.isSuperAdmin || this.isRestriction(slug)) return false;
      if (this.originalAbilities.includes(slug)) return false;
      return !this.$hasAbility(slug);
    },
    // "gerenciar" sem "ver" não faz sentido: marcar um puxa o outro, desmarcar "ver" leva o "gerenciar" junto.
    toggleAbility(slug, checked) {
      const related = checked
        ? [slug, slug.replace(/\.gerenciar$/, ".ver")]
        : [slug, slug.replace(/\.ver$/, ".gerenciar")];

      const editable = related.filter((item) => this.slugExists(item) && !this.isLocked(item));

      this.role.abilities = checked
        ? [...new Set([...this.role.abilities, ...editable])]
        : this.role.abilities.filter((item) => !editable.includes(item));
    },
    slugExists(slug) {
      return this.groups.some((group) => group.items.some((item) => item.slug === slug));
    },
    countSelected(group) {
      return group.items.filter((item) => this.isChecked(item.slug)).length;
    },
    isGroupFull(group) {
      return group.items
        .filter((item) => !this.isLocked(item.slug))
        .every((item) => this.isChecked(item.slug));
    },
    toggleGroup(group) {
      const marcar = !this.isGroupFull(group);
      const slugs = group.items.map((item) => item.slug).filter((slug) => !this.isLocked(slug));

      this.role.abilities = marcar
        ? [...new Set([...this.role.abilities, ...slugs])]
        : this.role.abilities.filter((slug) => !slugs.includes(slug));
    },
  },
  mounted() {
    this.getCatalog().then(() => {
      const id = this.$route.params.id;

      if (id) {
        this.getRole(id);
      }
    });
  },
  components: {
    ValidationObserver,
    ValidationProvider,
    BAlert,
    BBadge,
    BButton,
    BForm,
    BFormCheckbox,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
};
</script>

<style scoped>
.required {
  color: red !important;
}

.ability-group {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
}

.ability-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  background: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  border-radius: 6px 6px 0 0;
  font-size: 0.85rem;
}

.ability-group-title {
  font-weight: 600;
}

.ability-group-tools {
  font-size: 0.75rem;
  white-space: nowrap;
}

.ability-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px dashed #ebe9f1;
  font-size: 0.85rem;
}

.ability-item:last-child {
  border-bottom: 0;
}

.ability-item-locked {
  opacity: 0.55;
}

.ability-slug {
  flex-shrink: 0;
  font-size: 0.65rem;
  color: #82868b;
  background: #f3f2f7;
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
}
</style>
